import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, Trash2, Check, Flag, ExternalLink, MessageSquareOff } from "lucide-react";
import { toast } from "sonner";

export default function ReportsPanel({ api }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status: "pending" } });
      setReports(data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const act = async (commentId, action) => {
    setBusy(commentId + action);
    try {
      await api.post(`/admin/reports/${commentId}/${action}`);
      toast.success(action === "remove" ? "Comment removed" : "Reports dismissed");
      setReports((prev) => prev.filter((r) => r.comment_id !== commentId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-6 mb-6" data-testid="reports-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl text-dark flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-600" /> MODERATION QUEUE
          {reports.length > 0 && (
            <span className="text-xs font-bold text-white bg-red-600 px-2 py-0.5 rounded-full" data-testid="reports-count">{reports.length}</span>
          )}
        </h2>
        <button onClick={fetchReports} className="text-xs font-semibold text-neutral hover:text-dark" data-testid="reports-refresh">Refresh</button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-24 bg-gray-50 rounded-xl animate-pulse" />)}</div>
      ) : reports.length === 0 ? (
        <div className="text-center py-8 text-sm text-neutral flex flex-col items-center gap-2" data-testid="reports-empty">
          <MessageSquareOff className="w-8 h-8 text-gray-300" />
          No pending reports. The discussion is clean.
        </div>
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {reports.map((r) => (
            <div key={r.comment_id} className="border border-gray-100 rounded-xl p-4" data-testid={`report-${r.comment_id}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
                  <Flag className="w-3 h-3" /> {r.report_count} report{r.report_count !== 1 ? "s" : ""}
                </span>
                {r.is_reply && <span className="text-[10px] font-bold text-neutral bg-gray-100 px-2 py-0.5 rounded-full">REPLY</span>}
                {!r.comment_exists && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">ALREADY DELETED</span>}
                <Link to={`/issue/${r.topic_id}`} className="ml-auto inline-flex items-center gap-1 text-[11px] text-view-a hover:underline" data-testid={`report-topic-link-${r.comment_id}`}>
                  <ExternalLink className="w-3 h-3" /> View debate
                </Link>
              </div>

              <p className="text-[11px] text-neutral mb-1 line-clamp-1">On: {r.topic_question}</p>
              <p className="text-sm text-dark bg-gray-50 rounded-lg p-3 mb-2 whitespace-pre-wrap break-words">
                <span className="font-semibold">{r.author?.name || "Unknown"}:</span> {r.comment_text || "(empty)"}
              </p>

              <div className="text-[11px] text-neutral mb-3 space-y-0.5">
                {r.reporters.slice(0, 4).map((rp, i) => (
                  <p key={i}>⚑ {rp.name} — <span className="text-dark/70">{rp.reason || "no reason given"}</span></p>
                ))}
                {r.reporters.length > 4 && <p>+{r.reporters.length - 4} more</p>}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => act(r.comment_id, "remove")}
                  disabled={busy === r.comment_id + "remove"}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  data-testid={`report-remove-${r.comment_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove comment
                </button>
                <button
                  onClick={() => act(r.comment_id, "dismiss")}
                  disabled={busy === r.comment_id + "dismiss"}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-neutral bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                  data-testid={`report-dismiss-${r.comment_id}`}
                >
                  <Check className="w-3.5 h-3.5" /> Keep (dismiss)
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
